import { Logger } from "../../helpers/Logger";
import { getTrackingId } from "../config/CONSTANTS";

/**
 * @fileoverview Handles tracking events with Facebook Pixel.
 * @module FacebookPixel
 */

/**
 * Tracks an event and sends data to Facebook Pixel.
 * Initializes the pixel if not already initialized.
 *
 * ### Allowed Event Types and Data
 * - Common event types: `PageView`, `Lead`, `CompleteRegistration`, `AddToCart`, `Purchase`, etc.
 * - `eventData` can include properties like `currency`, `value`, `content_name`, `content_category`, etc.
 *
 * @example
 * trackFacebookPixelEvent('PageView', { page_path: '/home' });
 * trackFacebookPixelEvent('view-content', { content_name: 'Product Page' }); // Maps to 'PageView' or 'ViewContent'
 *
 * @param {string} eventType - The type of event to track.
 * @param {Object} eventData - The data to send with the event.
 */
export function trackFacebookPixelEvent(eventType, eventData) {
  // Define a mapping for non-standard event names to standard ones
  const eventMapping = {
    // 'view-content': 'ViewContent', // Use 'ViewContent' if it represents specific content like a product
    // Add other mappings as needed, e.g., 'add-to-cart': 'AddToCart'
  };

  // Map the eventType to a standard event name if it exists in the mapping, otherwise use the original
  const mappedEventType = eventMapping[eventType] || eventType;

  function initPixel() {
    if (typeof window === "undefined" || window.fbq) return;

    try {
      loadFacebookPixelScript();
      window.fbq("init", getTrackingId("Facebook_Pixel"));
    } catch (error) {
      Logger.error("Error initializing Facebook Pixel:", error);
    }
  }

  function loadFacebookPixelScript() {
    !(function (f, b, e, v, n, t, s) {
      if (f.fbq) return;
      n = f.fbq = function () {
        n.callMethod
          ? n.callMethod.apply(n, arguments)
          : n.queue.push(arguments);
      };
      if (!f._fbq) f._fbq = n;
      n.push = n;
      n.loaded = !0;
      n.version = "2.0";
      n.queue = [];
      t = b.createElement(e);
      t.async = !0;
      t.src = v;
      s = b.getElementsByTagName(e)[0];
      s.parentNode.insertBefore(t, s);
    })(
      window,
      document,
      "script",
      "https://connect.facebook.net/en_US/fbevents.js"
    );
  }

  initPixel();

  if (window.fbq) {
    window.fbq("track", mappedEventType, eventData);
    Logger.log(`Facebook Pixel event sent: ${mappedEventType}`, eventData);
  } else {
    Logger.error("Facebook Pixel not loaded");
  }
}